import React from "react";
import {
  Autocomplete,
  Box,
  FormControlLabel,
  Radio,
  RadioGroup,
  Stack,
  TextField,
  Typography,
} from "@mui/material";
import useGetAllInsuranceCodes from "hooks/api/insurance/useGetAllInsuranceCodes";

const ASSESSMENT_BILLING_OPTIONS = [
  { value: "INITIAL_ASSESSMENT", label: "Initial Treatment Plan" },
  { value: "RE_ASSESSMENT_OR_90_DAYS", label: "90-Day or Re-Assessment Treatment Plan" },
  { value: "BOTH", label: "Both" },
  { value: "NEITHER", label: "Neither" },
] as const;

interface CptOption {
  id: number;
  label: string;
}

export default function CptMappingTab({
  onChange,
  data,
}: {
  onChange: (name: string, type: null, data: any) => (event: any) => void;
  data: any;
}) {
  const { insuranceCodes: availableCptCodes } = useGetAllInsuranceCodes();

  const codeOptions: CptOption[] = (availableCptCodes ?? []).map((code) => ({
    id: code.id,
    label: code.insurance_code,
  }));

  const findOption = (id: number | null | undefined): CptOption | null =>
    codeOptions.find((opt) => opt.id === id) ?? null;

  const isSameOption = (opt: CptOption, val: CptOption | null) => opt.id === val?.id;

  const handleCptChange =
    (field: string) => (_: React.SyntheticEvent, selected: CptOption | null) => {
      onChange(field, null, data)({ target: { value: selected?.id ?? null, type: "select" } });
    };

  const handleBillingAtChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    onChange(
      "approve_assessment_billing_at",
      null,
      data
    )({
      target: { value: e.target.value, type: "select" },
    });
  };

  return (
    <Stack gap={5}>
      <Box>
        <Typography variant="subtitle2">CPT Mapping</Typography>
        <Typography variant="body2" color="text.secondary">
          Configure the payor-specific CPT code to service type mappings.
        </Typography>
      </Box>

      <Autocomplete
        options={codeOptions}
        getOptionLabel={(opt) => opt.label}
        value={findOption(data.assessment_cpt_code_id)}
        onChange={handleCptChange("assessment_cpt_code_id")}
        isOptionEqualToValue={isSameOption}
        renderInput={(params) => (
          <TextField {...params} label="Assessment" placeholder="Select CPT codes..." />
        )}
      />

      <Box sx={{ display: "flex", flexDirection: "column", gap: 1 }}>
        <Typography variant="caption" fontWeight={500} color="text.secondary">
          This plan approves Assessment billing at the following:
        </Typography>
        <RadioGroup
          value={data.approve_assessment_billing_at ?? ""}
          onChange={handleBillingAtChange}
          sx={{ display: "flex", flexDirection: "column", gap: "8px" }}
        >
          {ASSESSMENT_BILLING_OPTIONS.map(({ value, label }) => (
            <FormControlLabel
              key={value}
              value={value}
              control={<Radio size="small" sx={{ p: 0 }} />}
              label={label}
              slotProps={{ typography: { fontSize: 14 } }}
              sx={{ m: 0, gap: "8px" }}
            />
          ))}
        </RadioGroup>
      </Box>

      <Autocomplete
        options={codeOptions}
        getOptionLabel={(opt) => opt.label}
        value={findOption(data.direct_therapy_cpt_code_id)}
        onChange={handleCptChange("direct_therapy_cpt_code_id")}
        isOptionEqualToValue={isSameOption}
        renderInput={(params) => (
          <TextField {...params} label="Direct Therapy" placeholder="Select CPT codes..." />
        )}
      />

      <Autocomplete
        options={codeOptions}
        getOptionLabel={(opt) => opt.label}
        value={findOption(data.caregiver_training_cpt_code_id)}
        onChange={handleCptChange("caregiver_training_cpt_code_id")}
        isOptionEqualToValue={isSameOption}
        renderInput={(params) => (
          <TextField {...params} label="Caregiver Training" placeholder="Select CPT codes..." />
        )}
      />

      <Autocomplete
        options={codeOptions}
        getOptionLabel={(opt) => opt.label}
        value={findOption(data.caregiver_mediated_cpt_code_id)}
        onChange={handleCptChange("caregiver_mediated_cpt_code_id")}
        isOptionEqualToValue={isSameOption}
        renderInput={(params) => (
          <TextField {...params} label="Caregiver Mediated" placeholder="Select CPT codes..." />
        )}
      />

      <Autocomplete
        options={codeOptions}
        getOptionLabel={(opt) => opt.label}
        value={findOption(data.protocol_modification_direct_cpt_code_id)}
        onChange={handleCptChange("protocol_modification_direct_cpt_code_id")}
        isOptionEqualToValue={isSameOption}
        renderInput={(params) => (
          <TextField
            {...params}
            label="Direct Protocol Modification"
            placeholder="Select CPT codes..."
          />
        )}
      />

      <Autocomplete
        options={codeOptions}
        getOptionLabel={(opt) => opt.label}
        value={findOption(data.protocol_modification_indirect_cpt_code_id)}
        onChange={handleCptChange("protocol_modification_indirect_cpt_code_id")}
        isOptionEqualToValue={isSameOption}
        renderInput={(params) => (
          <TextField
            {...params}
            label="Indirect Protocol Modification"
            placeholder="Select CPT codes..."
          />
        )}
      />
    </Stack>
  );
}
